import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UserService } from '../../services/user.service';
import { CreateBusinessUserPayload } from '../../interfaces';
import countriesData from '../../../../countries.json';

@Component({
  selector: 'app-create-b2b-user-modal',
  standalone: false,
  templateUrl: './create-b2b-user-modal.component.html',
  styleUrl: './create-b2b-user-modal.component.css',
})
export class CreateB2bUserModalComponent implements OnChanges {
  @Input() show = false;
  @Input() userUuid = '';
  @Input() defaultCountry = '';

  @Output() close = new EventEmitter<void>();
  @Output() userCreated = new EventEmitter<{ uuid: string; response: any }>();

  registered_name = '';
  registration_number = '';
  tax_identification_number = '';
  entity_type = 'LimitedLiabilityCompany';
  registration_country = '';
  incorporation_date = '';
  street = '';
  city = '';
  state = '';
  postal_code = '';
  country = '';

  isSubmitting = false;
  errorMessage: string | null = null;

  entityTypes = [
    { value: 'LimitedLiabilityCompany', label: 'Limited Liability Company' },
    { value: 'PublicCompany', label: 'Public Company' },
    { value: 'SoleProprietorship', label: 'Sole Proprietorship' },
    { value: 'Partnership', label: 'Partnership' },
    { value: 'Corporation', label: 'Corporation' },
    { value: 'Trust', label: 'Trust' },
    { value: 'PrivateFoundation', label: 'Private Foundation' },
    { value: 'Charity', label: 'Charity' },
    { value: 'NonProfitOrganization', label: 'Non-Profit Organization' },
    { value: 'PublicAgency', label: 'Public Agency' },
  ];

  countries = countriesData.countries;



  get maxDate(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  constructor(private userService: UserService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show']?.currentValue && this.defaultCountry) {
      if (!this.registration_country) {
        this.registration_country = this.defaultCountry;
      }
      if (!this.country) {
        this.country = this.defaultCountry;
      }
    }
  }

  resetForm() {
    this.registered_name = '';
    this.registration_number = '';
    this.tax_identification_number = '';
    this.entity_type = 'LimitedLiabilityCompany';
    this.registration_country = this.defaultCountry || '';
    this.incorporation_date = '';
    this.street = '';
    this.city = '';
    this.state = '';
    this.postal_code = '';
    this.country = this.defaultCountry || '';
    this.isSubmitting = false;
    this.errorMessage = null;
  }

  onRegistrationCountryChange(val: string) {
    this.registration_country = val;
    if (!this.country) {
      this.country = val;
    }
  }

  onCountryChange(val: string) {
    this.country = val;
    if (!this.registration_country) {
      this.registration_country = val;
    }
  }

  onClose() {
    this.resetForm();
    this.close.emit();
  }

  onSubmit() {
    if (
      !this.registered_name.trim() ||
      !this.registration_number.trim() ||
      !this.tax_identification_number.trim() ||
      !this.entity_type ||
      !this.registration_country.trim() ||
      !this.incorporation_date ||
      !this.street.trim() ||
      !this.city.trim() ||
      !this.state.trim() ||
      !this.postal_code.trim() ||
      !this.country.trim()
    ) {
      this.errorMessage = 'Please fill out all required fields.';
      return;
    }

    if (this.incorporation_date > this.maxDate) {
      this.errorMessage = 'Incorporation date cannot be in the future.';
      return;
    }

    if (!window.confirm('Are you sure you want to create this business user?')) {
      return;
    }


    const payload: CreateBusinessUserPayload = {
      registered_name: this.registered_name.trim(),
      registration_number: this.registration_number.trim(),
      tax_identification_number: this.tax_identification_number.trim(),
      entity_type: this.entity_type,
      registration_country: this.registration_country.trim().toUpperCase(),
      incorporation_date: this.incorporation_date,
      street: this.street.trim(),
      city: this.city.trim(),
      state: this.state.trim(),
      postal_code: this.postal_code.trim(),
      country: this.country.trim().toUpperCase(),
    };

    const targetUuid = this.userUuid.trim();
    if (!targetUuid) {
      this.errorMessage = 'Target user UUID is missing.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = null;

    this.userService.createBusinessUser(targetUuid, payload).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.userCreated.emit({ uuid: targetUuid, response });
        this.resetForm();
      },
      error: (err) => {
        this.isSubmitting = false;
        let msg = '';

        if (err?.error && typeof err.error.message === 'string') {
          msg = err.error.message;
        } else if (err?.error && Array.isArray(err.error.message)) {
          msg = err.error.message.join(', ');
        } else if (err?.error && typeof err.error.error === 'string') {
          msg = err.error.error;
        } else if (err?.error && typeof err.error.detail === 'string') {
          msg = err.error.detail;
        } else if (typeof err?.error === 'string' && err.error.length < 300 && !err.error.includes('<html')) {
          msg = err.error;
        }

        if (!msg) {
          if (err?.status === 500) {
            msg = 'Server Error (500): An unexpected error occurred on the server. Please try again later or check server logs.';
          } else if (err?.status === 404) {
            msg = 'User or resource not found (404).';
          } else if (err?.status === 400) {
            msg = 'Bad Request (400): Please check the entered business and address information.';
          } else if (err?.message) {
            msg = err.message;
          } else {
            msg = 'Failed to create business user. Please try again.';
          }
        } else if (err?.status === 500) {
          msg = `Server Error (500): ${msg}`;
        }

        this.errorMessage = msg;
      },

    });
  }
}
